// Coach digest — the latest call's suggestions as decision cards
// (APPLY / SNOOZE / DISMISS), the detail-horizon warning, and the on-demand
// coach trigger. The full call history stays on /review.
//
// APPLY ALL works exactly as it does on /review and in the native app: one
// action for the sessions (activity + details, applied server-side in one
// request) and one for the meals. Meals are whole-plan by nature — the daily
// target is the sum of every meal — so they have no per-meal APPLY; applying
// one already rewrote the whole target while leaving its siblings pending.

import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { formatApiError } from '../../lib/api';
import {
  useApplyAllMacrosMutation,
  useApplyMutation,
  useBulkApplyMutation,
  useDismissMutation,
  useSnoozeMutation,
} from '../../lib/coach-actions';
import { splitNarrative } from '../../lib/coach-narrative';
import { useOpenAdjustment } from '../../lib/coach-adjust';
import { AdjustmentProposal } from '../AdjustmentProposal';
import { CoachBriefView } from '../CoachBriefView';
import { deriveHorizonStatus, useCoachHorizon } from '../../lib/coach-horizon';
import { useCoachLatest } from '../../lib/day-data';
import { C, FONT_DISPLAY, FONT_MONO } from '../../lib/design';
import { dayLabel } from '../../lib/ledger';
import type { CoachLatestResponse, CoachLatestSuggestion } from '../../lib/types';
import { RunCoachButton } from '../review/RunCoachButton';
import { Btn, ErrorStrip, Lbl, Note, Sub } from './ui';

// The suggestion payload is `unknown` on the wire (shape varies by
// dimension); this is the union of fields the digest reads, all optional.
type Payload = {
  date?: string;
  week_start_date?: string;
  kind?: string;
  category?: string;
  name?: string;
  reason?: string;
  description?: string;
  summary?: string;
  focus?: string;
  meal?: string;
  kcal?: number;
  protein_g?: number;
  fat_g?: number;
  carb_g?: number;
  duration_min?: number;
  zone?: string;
  target_pace_s_per_km?: number | null;
  exercises?: unknown[];
  warning?: string;
};

const STATUS_COLOR: Record<CoachLatestSuggestion['status'], string> = {
  pending: C.warn,
  applied: C.signal,
  dismissed: C.faint,
  snoozed: C.faint,
};

/** The call's prose as paragraphs. Folded to the first paragraph so the
 *  decision cards stay above the fold; `MORE` opens the rest in place. */
function Narrative({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const paragraphs = useMemo(() => splitNarrative(text), [text]);
  if (paragraphs.length === 0) return null;
  const shown = open ? paragraphs : paragraphs.slice(0, 1);
  return (
    <Note label="◆ NARRATIVE">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((paragraph, i) => (
          <p key={i} style={{ margin: 0, lineHeight: 1.6 }}>
            {paragraph}
          </p>
        ))}
      </div>
      {paragraphs.length > 1 && (
        <div style={{ marginTop: 8 }}>
          <Btn ghost onClick={() => setOpen((o) => !o)}>
            {open ? 'LESS' : `MORE · ${paragraphs.length - 1}`}
          </Btn>
        </div>
      )}
    </Note>
  );
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function summary(s: CoachLatestSuggestion, p: Payload): string {
  switch (s.dimension) {
    case 'activity':
      return p.kind === 'rest' ? 'Rest' : [p.name, p.category].filter(Boolean).join(' · ') || 'Activity';
    case 'details': {
      if (p.kind === 'run') {
        const pace =
          p.target_pace_s_per_km != null
            ? ` · ${Math.floor(p.target_pace_s_per_km / 60)}:${String(p.target_pace_s_per_km % 60).padStart(2, '0')}/km`
            : '';
        return `Run · ${p.duration_min ?? '?'} min${p.zone ? ` @ ${p.zone}` : ''}${pace}`;
      }
      if (p.kind === 'strength') return `Strength · ${p.exercises?.length ?? 0} exercises`;
      return p.description ?? p.kind ?? 'Details';
    }
    case 'macros':
      return `${(p.meal ?? 'meal').toUpperCase()} · ${p.kcal ?? '?'} kcal · P${p.protein_g ?? '?'} F${p.fat_g ?? '?'} C${p.carb_g ?? '?'}`;
    case 'plan':
      return p.name ?? p.focus ?? p.summary ?? 'Plan';
  }
}

function Card({
  s,
  warnings,
  busy,
  onApply,
  onSnooze,
  onDismiss,
}: {
  s: CoachLatestSuggestion;
  warnings: string[];
  busy: boolean;
  onApply: () => void;
  onSnooze: () => void;
  onDismiss: () => void;
}) {
  const p = (s.suggestion ?? {}) as Payload;
  // Payload dates are coach-written; only format ones that are real ISO days.
  const dayOf = (iso: string | undefined) => (iso && ISO_DAY.test(iso) ? dayLabel(iso) : null);
  const day = dayOf(p.date) ?? (dayOf(p.week_start_date) ? `WK ${dayOf(p.week_start_date)}` : null);
  const actionable = s.status === 'pending' || s.status === 'snoozed';
  const warnLines = p.warning ? [p.warning, ...warnings] : warnings;
  // For details cards the description is already the summary line.
  const reason = s.dimension === 'details' ? p.reason : p.reason ?? p.description;
  return (
    <div style={{ border: `1px solid ${C.line}`, padding: 12, opacity: s.status === 'pending' ? 1 : 0.55 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Lbl>
          {s.dimension}
          {day ? ` · ${day}` : ''}
        </Lbl>
        <Lbl color={STATUS_COLOR[s.status]}>
          {s.status}
          {s.status === 'snoozed' && s.snooze_until ? ` · ${dayLabel(s.snooze_until)}` : ''}
        </Lbl>
      </div>
      <div style={{ fontSize: 13, marginTop: 6 }}>{summary(s, p)}</div>
      {reason && <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.55, marginTop: 6 }}>{reason}</div>}
      {warnLines.map((w, i) => (
        <div key={i} style={{ fontSize: 10, color: C.warn, marginTop: 6, letterSpacing: '0.05em', lineHeight: 1.5 }}>
          ⚠ {w}
        </div>
      ))}
      {actionable && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          {s.dimension !== 'macros' && (
            <Btn primary disabled={busy} onClick={onApply}>APPLY</Btn>
          )}
          {s.status === 'pending' && <Btn disabled={busy} onClick={onSnooze}>SNOOZE</Btn>}
          <Btn disabled={busy} onClick={onDismiss}>DISMISS</Btn>
        </div>
      )}
    </div>
  );
}

/** Rail warnings keyed to the details suggestion they index (server order). */
function warningsById(data: CoachLatestResponse): Record<string, string[]> {
  const details = data.suggestions.filter((s) => s.dimension === 'details');
  const out: Record<string, string[]> = {};
  for (const w of data.warnings) {
    if (w.suggestion_index == null) continue;
    const target = details[w.suggestion_index];
    if (!target) continue;
    (out[target.id] ??= []).push(`${w.rail} · ${w.detail}`);
  }
  return out;
}

export function CoachInspector() {
  const latest = useCoachLatest();
  const horizonQ = useCoachHorizon();
  const apply = useApplyMutation();
  const snooze = useSnoozeMutation();
  const dismiss = useDismissMutation();
  const bulkApply = useBulkApplyMutation();
  const applyMeals = useApplyAllMacrosMutation();
  const openAdjustmentId = useOpenAdjustment().data?.coach_call_id ?? null;
  const busy =
    apply.isPending ||
    snooze.isPending ||
    dismiss.isPending ||
    bulkApply.isPending ||
    applyMeals.isPending;
  const actionError =
    apply.error ?? snooze.error ?? dismiss.error ?? bulkApply.error ?? applyMeals.error;

  const data = latest.data ?? null;
  const pending = data ? data.suggestions.filter((s) => s.status === 'pending').length : 0;
  // Snoozed rows count as open: the server's bulk apply takes them too.
  const isOpen = (s: CoachLatestSuggestion) => s.status === 'pending' || s.status === 'snoozed';
  const openSessions = data
    ? data.suggestions.filter(
        (s) => (s.dimension === 'activity' || s.dimension === 'details') && isOpen(s),
      ).length
    : 0;
  const meals = data ? data.suggestions.filter((s) => s.dimension === 'macros') : [];
  const openMeals = meals.filter(isOpen).length;
  const horizon = horizonQ.data?.horizon ? deriveHorizonStatus(horizonQ.data.horizon) : null;
  const byId = data ? warningsById(data) : {};
  // Arc-level rail warnings index nothing; they still deserve a line.
  const unindexed = data ? data.warnings.filter((w) => w.suggestion_index == null) : [];

  return (
    <div>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.line}` }}>
        <Lbl color={C.signal}>
          ◆ COACH{data ? ` · WEEKLY CALL · ${dayLabel(data.coach_call.date_lisbon)}` : ''}
        </Lbl>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, marginTop: 6 }}>
          {latest.isLoading
            ? 'Loading…'
            : !data
              ? 'No coach call yet'
              : `${pending} pending decision${pending === 1 ? '' : 's'}`}
        </div>
      </div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* First, so a proposal made before a reload (or on another device)
            is the first thing the digest asks about. */}
        {openAdjustmentId != null && <AdjustmentProposal coachCallId={openAdjustmentId} />}
        {latest.error && <ErrorStrip message={formatApiError(latest.error)} />}
        {horizon?.expired && horizon.horizon && (
          <Note label="PAST DETAIL HORIZON" color={C.warn} style={{ borderColor: C.warn }}>
            The detailed plan ended {dayLabel(horizon.horizon.detailed_plan_end_date)} · run the coach for a fresh prescription.
          </Note>
        )}
        {data?.coach_call.brief ? (
          <CoachBriefView brief={data.coach_call.brief} />
        ) : (
          // Calls from before the brief existed carry prose only.
          data?.coach_call.narrative && <Narrative text={data.coach_call.narrative} />
        )}
        {data && data.suggestions.length === 0 && <Sub>NO SUGGESTIONS IN THIS CALL</Sub>}
        {data && (openSessions > 0 || openMeals > 0) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: '10px 12px',
              border: `1px solid ${C.signal}`,
            }}
          >
            {openSessions > 0 && (
              <Btn
                primary
                disabled={busy}
                onClick={() => bulkApply.mutate({ coachCallId: data.coach_call.id })}
              >
                {bulkApply.isPending
                  ? 'APPLYING…'
                  : `APPLY ALL · ${openSessions} SESSION${openSessions === 1 ? '' : 'S'}`}
              </Btn>
            )}
            {openMeals > 0 && (
              <Btn
                primary
                disabled={busy}
                onClick={() => applyMeals.mutate({ suggestions: meals })}
              >
                {applyMeals.isPending
                  ? 'APPLYING…'
                  : `APPLY ALL · ${openMeals} MEAL${openMeals === 1 ? '' : 'S'}`}
              </Btn>
            )}
          </div>
        )}
        {data?.suggestions.map((s) => (
          <Card
            key={s.id}
            s={s}
            warnings={byId[s.id] ?? []}
            busy={busy}
            onApply={() => apply.mutate({ suggestion: s })}
            onSnooze={() => snooze.mutate({ suggestion: s })}
            onDismiss={() => dismiss.mutate({ suggestion: s })}
          />
        ))}
        {unindexed.map((w, i) => (
          <div key={i} style={{ fontSize: 10, color: C.warn, letterSpacing: '0.05em', lineHeight: 1.5 }}>
            ⚠ {w.rail} · {w.detail}
          </div>
        ))}
        {actionError && <ErrorStrip message={formatApiError(actionError)} />}
        <div style={{ marginTop: 4 }}>
          <RunCoachButton />
        </div>
        <Link
          to="/review"
          style={{
            alignSelf: 'flex-start',
            fontFamily: FONT_MONO,
            fontSize: 10,
            letterSpacing: '0.15em',
            color: C.signal,
            textDecoration: 'none',
            padding: '4px 0',
          }}
        >
          FULL REVIEW →
        </Link>
      </div>
    </div>
  );
}
